import { createSlice, PayloadAction } from '@reduxjs/toolkit';

import { Valuation } from '@/global/tradein/indicata.interface';
import { RootState } from '@/store/redux';

export interface IValuationInfo {
  regDate: string;
  seats?: number;
  tracerId?: string | undefined;
  trimId?: number | undefined;
  body?: string | undefined;
  bodyType?: string | undefined;
  doors?: string | undefined;
  engine?: string | undefined;
  displacement?: string | undefined;
  powerHP?: string | undefined;
  powerPS?: string | undefined;
  powerKW?: string | undefined;
  transmission?: string | undefined;
  transmissionType?: string | undefined;
  transmissionSteps?: string | undefined;
  fuel?: string | undefined;
  make: string | undefined;
  model: string | undefined;
  trim?: string | undefined;
  wheelDrive?: string | undefined;
  vin?: string;
}

export type SavedGarageVehicle = {
  image: string | undefined | null;
  id: string | undefined | null;
};

export type ValuationState = {
  valuationLinks?: Valuation[] | null;
  valuationInfo?: IValuationInfo | null;
  garageVehicle?: SavedGarageVehicle | null;
};

const initialState: ValuationState = {
  valuationLinks: null,
  valuationInfo: null,
  // TODO Is this now covered by route parameter? If so, can it be removed?
  garageVehicle: null,
};

/**
 * When the vehicle identification is near-complete (such that INDICATA is able to valuate
 * the vehicle) a valuation object will appear in the response that will be pushed into state.
 */
const slice = createSlice({
  name: 'tradein/valuation',
  initialState,
  reducers: {
    // Add valuation link when identification was successful
    setValuationLinks(
      state,
      action: PayloadAction<{
        valuationLinks: Valuation[];
        valuationInfo: IValuationInfo;
      }>
    ) {
      return action.payload;
    },
    setCurrentVehicle(
      state,
      action: PayloadAction<{ garageVehicle: SavedGarageVehicle }>
    ) {
      const { garageVehicle } = action.payload;
      return { ...state, garageVehicle };
    },
    setRegistrationValuation(
      state,
      action: PayloadAction<{ regDate: string }>
    ) {
      const { regDate } = action.payload;
      if (state.valuationInfo) {
        state.valuationInfo.regDate = regDate;
      } else {
        state.valuationInfo = {
          make: undefined,
          model: undefined,
          regDate: regDate,
        };
      }
      return state;
    },
    resetValuationLinks() {
      return initialState;
    },
  },
});

export const {
  setValuationLinks,
  setCurrentVehicle,
  resetValuationLinks,
  setRegistrationValuation,
} = slice.actions;

export const getTradeInValuationState = (state: RootState): ValuationState =>
  state.pages.tradeIn.valuation;

export default slice.reducer;
